/**
 * useRoomTimerSync — TV-P3-05
 *
 * Bridges LiveKit RoomEvents to the visitStore timer.
 *
 * Contract (per the dev plan AC):
 *   - Provider closes tab → patient's timer pauses.
 *   - Provider reconnects → patient's timer resumes.
 *
 * Implementation:
 *   - Track the count of non-self participants in the room.
 *   - 1 → 0 transition (counterpart left): pauseVisit().
 *   - 0 → ≥1 transition (counterpart joined a paused visit):
 *     resumeVisit().
 *
 * Why "non-self count" instead of role-gated logic:
 *   - Works symmetrically from both sides — provider sees patient
 *     drop too, and the visit pausing on patient drop is the right
 *     billing-time accounting either way.
 *   - Multi-party (TV-P3-06 interpreter): visit only pauses if ALL
 *     remotes leave, so an interpreter dropping doesn't pause the
 *     primary visit timer.
 *
 * Mount this hook INSIDE a LiveKitRoom — it pulls events off the
 * room context. Mounting outside is a no-op.
 */

import { useEffect, useRef } from 'react';
import { useRoomContext, useRemoteParticipants } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';
import { useVisitStore } from '@/stores/visitStore';

export function useRoomTimerSync() {
  const room = useRoomContext();
  const remotes = useRemoteParticipants();
  const pauseVisit = useVisitStore((s) => s.pauseVisit);
  const resumeVisit = useVisitStore((s) => s.resumeVisit);
  const visitStatus = useVisitStore((s) => s.visitStatus);

  // Track the previous remote count so we can detect transitions
  // rather than firing pause/resume on every list mutation.
  const prevCount = useRef(remotes.length);

  useEffect(() => {
    const current = remotes.length;
    const previous = prevCount.current;

    if (previous > 0 && current === 0) {
      // Last counterpart just left.
      if (useVisitStore.getState().visitStatus === 'in-progress') {
        // eslint-disable-next-line no-console
        console.info('[useRoomTimerSync] all remotes left — pausing visit');
        pauseVisit();
      }
    } else if (previous === 0 && current > 0) {
      // First counterpart joined (or rejoined) a paused visit.
      if (useVisitStore.getState().visitStatus === 'paused') {
        // eslint-disable-next-line no-console
        console.info('[useRoomTimerSync] remote rejoined — resuming visit');
        resumeVisit();
      }
    }

    prevCount.current = current;
  }, [remotes.length, pauseVisit, resumeVisit]);

  // Belt-and-suspenders: also listen for the discrete events so we
  // log even if the participant list doesn't re-render for some
  // reason (helpful for debugging in dev).
  useEffect(() => {
    if (!room) return undefined;
    const onConnected = (participant) => {
      // eslint-disable-next-line no-console
      console.debug('[useRoomTimerSync] ParticipantConnected', {
        identity: participant?.identity,
      });
    };
    const onDisconnected = (participant) => {
      // eslint-disable-next-line no-console
      console.debug('[useRoomTimerSync] ParticipantDisconnected', {
        identity: participant?.identity,
      });
    };
    room.on(RoomEvent.ParticipantConnected, onConnected);
    room.on(RoomEvent.ParticipantDisconnected, onDisconnected);
    return () => {
      room.off(RoomEvent.ParticipantConnected, onConnected);
      room.off(RoomEvent.ParticipantDisconnected, onDisconnected);
    };
  }, [room]);

  return { remoteCount: remotes.length, visitStatus };
}
